import { Fragment, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getV31Products } from '@/api/Products';
import { useRouter } from 'next/router';
import { ProductCatalogueV2, FilterChipsList, SnackBar, OffersSnackbar } from '@desquared/wave-components-library';
import useProductTags from '@/hooks/useProductTags';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import {
  groupProductsByCategory,
  sortByPosition,
  getSortedSubcategoriesFromTree,
  getProductsWithFixedCategory,
  getProductsWithCategory,
} from '@/utils/category';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs';
import {
  addProduct,
  removeProduct,
  openListSideSheet,
  transformProduct,
  getBundleOfferDescription,
} from '@/utils/products';
import { checkListStatus } from '@/utils/lists';
import { productsForOrder } from '@/store/cart';
import CategoryTitle from '@/components/CategoryTitle/CategoryTitle';
import { useTranslations } from 'next-intl';
import styles from '../FullEditCatalogue.module.css';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import useGetDefaultProductImage from '@/hooks/useGetDefaultProductImage';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';

export function EditCatalogue({
  selectedCategoryId,
  categoriesFromFile,
  currentCategory,
  subCategories,
  selectedCategoryTree,
  route,
  setRoute,
  onSelect,
  selectedOrder,
}) {
  const { formatter } = useContext(CurrencyContext);
  const t = useTranslations();
  const a = useTranslations('common.asides');

  const router = useRouter();
  const observer = useRef(null);
  const { getCollectionTypes } = useContext(DeliveryServicesContext);
  const [products, setProducts] = useState([]);
  const [filterChipItems, setFilterChipsItems] = useState([]);
  const { subcategory } = route;
  const { personalizedProductsIds } = usePersonalizedProductsIds();

  const [productsOrder, setProductsOrder] = useState([]);
  const { productTags } = useProductTags();
  const { bundleOffersSummary } = useBundleOffersSummary();
  const [showSnackBar, setShowSnackbar] = useState(false);
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const defaultProductImage = useGetDefaultProductImage(0);
  const categorySubtitle = currentCategory.description
    ? currentCategory.description
    : t('category_details.subtitle', { categoryTitle: currentCategory?.name });

  useEffect(() => {
    const productsForOrder$ = productsForOrder.subscribe(setProductsOrder);
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);
    return () => {
      productsForOrder$.unsubscribe();
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);

  const fetchPageForCategoryProducts = async ({ pageParam }) => {
    const newProducts = await getV31Products(
      {
        pageNumber: pageParam.toString(),
        category: subcategory ? subcategory._id : selectedCategoryId,
        collectionTypes: getCollectionTypes(),
        categorySearchType: 'ALL_LEVELS',
        shop: selectedOrder?.shop?._id,
      },
      router.locale || DEFAULT_LOCALE
    );

    return newProducts;
  };

  // TODO: utilize error state
  const {
    data: productsResponse,
    error,
    fetchNextPage,
    isLoading: isLoadingProducts,
    isFetchingNextPage: isFetchingProductsNextPage,
  } = useInfiniteQuery({
    queryKey: ['edit-order-products', selectedCategoryId, route],
    queryFn: fetchPageForCategoryProducts,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.length === 0) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    enabled: !!selectedCategoryId,
  });

  useEffect(() => {
    if (productsResponse) {
      const newProducts = subcategory
        ? getProductsWithFixedCategory(
            productsResponse.pages.flatMap(p => p.map(enhanceProductWithSlug)),
            categoriesFromFile,
            productTags,
            bundleOffersSummary,
            router.locale,
            subCategories.find(i => i._id === subcategory._id).name,
            personalizedProductsIds,
            a
          )
        : getProductsWithCategory(
            productsResponse.pages.flatMap(p => p.map(enhanceProductWithSlug)),
            !route.slug.length === 1
              ? filterChipItems.length > 1
                ? filterChipItems
                    .filter(i => i._id)
                    .map(i => {
                      return { _id: i._id, name: i.name };
                    })
                : getSortedSubcategoriesFromTree(selectedCategoryTree[0])
              : getSortedSubcategoriesFromTree(selectedCategoryTree[0]),
            productTags,
            bundleOffersSummary,
            router.locale,
            { showSecondLevelCategory: true },
            personalizedProductsIds,
            a
          );
      setProducts(newProducts);
    }
  }, [
    bundleOffersSummary,
    filterChipItems,
    productTags,
    productsResponse,
    router.locale,
    selectedCategoryTree,
    route.slug.length,
    subcategory,
    categoriesFromFile,
    subCategories,
    personalizedProductsIds,
  ]);

  useEffect(() => {
    setFilterChipsItems([
      { text: t('category_details.all_categories'), selected: !subcategory },
      ...sortByPosition(
        subCategories.map(i => {
          return { text: i.name, ...i, selected: i._id === subcategory?._id };
        })
      ),
    ]);
  }, [subCategories, setFilterChipsItems, subcategory?._id, t, subcategory]);

  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (isLoadingProducts || isFetchingProductsNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      observer.current.observe(node);
    },
    [isLoadingProducts, isFetchingProductsNextPage, fetchNextPage]
  );

  return (
    <div className={styles.editCatalogue}>
      <CategoryTitle title={currentCategory.name} subtitle={categorySubtitle} />
      {isLoadingProducts ? (
        <ProductCatalogueV2 products={[]} isLoading loaderTilesAmount={15} />
      ) : (
        <>
          {filterChipItems.length > 1 ? (
            <div>
              <FilterChipsList
                items={filterChipItems}
                onChange={item => {
                  setRoute({
                    slug: route.slug,
                    subcategory: item.text === t('category_details.all_categories') ? undefined : item,
                  });
                }}
              />
            </div>
          ) : null}
          {groupProductsByCategory(products, route.slug.length ? null : currentCategory).map((i, idx) => {
            return (
              <Fragment key={idx}>
                <ProductCatalogueV2
                  title={i.category}
                  products={i.products}
                  offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                  isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                  ctaIcon={process.env.ctaIcon}
                  listIcon={process.env.listIcon}
                  lastItemRef={lastResultItemRef}
                  onListClick={openListSideSheet}
                  checkStatus={checkListStatus}
                  addProduct={product =>
                    addProduct(
                      product,
                      false,
                      {
                        item_list_id: 'edit',
                        item_list_name: 'edit',
                      },
                      false,
                      t('common.asides.side_cart.no_comment')
                    )
                  }
                  removeProduct={product =>
                    removeProduct(
                      product,
                      {
                        item_list_id: 'edit',
                        item_list_name: 'edit',
                      },
                      t('common.asides.side_cart.no_comment')
                    )
                  }
                  onProductDetails={onSelect}
                  setMaxSnackbar={setShowSnackbar}
                  currencyFormatter={formatter}
                  defaultProductImage={defaultProductImage}
                  forYouLabel={t('common.user.profile.menu.for_you')}
                />
              </Fragment>
            );
          })}
          {isFetchingProductsNextPage ? <ProductCatalogueV2 products={[]} isLoading loaderTilesAmount={2} /> : null}
        </>
      )}
      <SnackBar open={showSnackBar} setOpen={setShowSnackbar} text={t('snackbar')} position={'center'} />
      {showBundleOffersSnackBar && (
        <OffersSnackbar
          emoji="🎉"
          open={showBundleOffersSnackBar}
          setOpen={BundleOfferSnackBarOpen.update}
          hasCloseOption={true}
          position="center"
          type="warning"
          autoWidth={true}
          buttonText={t('common.offers_snack_bar.btn_label')}
          text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
          onButtonPress={() => {
            setShowBundleOffersSnackBar(false);
            BundleOfferSnackBarOpen.update(false);
            BundleOfferSideSheetStore.update(true);
          }}
          autoHideDuration={8000}
          closeOnClickAway={true}
          hasShadow={true}
        />
      )}
    </div>
  );
}
